import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getDirectorAnnotations,
  listEpisodes,
  saveDirectorAnnotations,
  startDirectorMapping,
} from '../../api';
import { actionErrorMessage } from '../episode-detail/components/DirectorMappingControls';
import type { DirectorAnnotationWorkspace, DirectorMark, Episode } from '../../types';
import { selectionToCodePointRange } from './ranges';
import './DirectorAnnotationsPage.css';

const QUICK_LABELS = [
  { value: '高光', key: 'highlight' },
  { value: '反转', key: 'reversal' },
  { value: '伏笔', key: 'foreshadowing' },
  { value: '重点', key: 'keyPoint' },
] as const;
const QUICK_LABEL_VALUES: string[] = QUICK_LABELS.map(item => item.value);
const MAX_MARKS = 200;
const MAX_MARK_RUNES = 2_000;
const MAX_LABELS = 10;
const MAX_LABEL_RUNES = 32;
const MAX_NOTES = 10;
const MAX_NOTE_RUNES = 2_000;
const MAX_BODY_BYTES = 1024 * 1024;

interface EditorMark extends DirectorMark {
  isNew: boolean;
}

function runeLength(value: string): number {
  return Array.from(value).length;
}

function hasInvalidLabelCharacter(value: string): boolean {
  return Array.from(value).some(character => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f
      || (codePoint >= 0x7f && codePoint <= 0x9f)
      || codePoint === 0x2028
      || codePoint === 0x2029;
  });
}

function hasInvalidNoteCharacter(value: string): boolean {
  return Array.from(value).some(character => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint !== 0x0a
      && codePoint !== 0x0d
      && (codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f));
  });
}

function normalizeMark(mark: DirectorMark): DirectorMark {
  return {
    ...mark,
    label: Array.isArray(mark.label) ? mark.label : [],
    note: Array.isArray(mark.note) ? mark.note : [],
  };
}

function cloneForEditor(mark: DirectorMark, isNew = false): EditorMark {
  const normalized = normalizeMark(mark);
  return {
    ...normalized,
    range: { ...normalized.range },
    label: [...normalized.label],
    note: normalized.note.length > 0 ? [...normalized.note] : [''],
    isNew,
  };
}

function newMarkId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `mark-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function rangesOverlap(left: DirectorMark['range'], right: DirectorMark['range']): boolean {
  return left.start < right.end && right.start < left.end;
}

function cleanMark(mark: EditorMark): DirectorMark {
  return {
    id: mark.id,
    range: { ...mark.range },
    mark: mark.mark,
    label: mark.label.map(value => value.trim()).filter(Boolean),
    note: mark.note.map(value => value.trim()).filter(Boolean),
  };
}

function bodyByteLength(value: unknown): number {
  const serialized = JSON.stringify(value);
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(serialized).length;
  return new Blob([serialized]).size;
}

function validateMarks(marks: DirectorMark[], t: (key: string, options?: Record<string, unknown>) => string): string | null {
  if (marks.length > MAX_MARKS) return t('directorAnnotations.errors.maxMarks', { count: MAX_MARKS });
  for (const mark of marks) {
    if (runeLength(mark.mark) > MAX_MARK_RUNES) return t('directorAnnotations.errors.maxMarkRunes', { count: MAX_MARK_RUNES });
    if (mark.label.length > MAX_LABELS) return t('directorAnnotations.errors.maxLabels', { count: MAX_LABELS });
    if (mark.note.length > MAX_NOTES) return t('directorAnnotations.errors.maxNotes', { count: MAX_NOTES });
    if (mark.label.some(label => runeLength(label) > MAX_LABEL_RUNES)) {
      return t('directorAnnotations.errors.maxLabelRunes', { count: MAX_LABEL_RUNES });
    }
    if (mark.label.some(hasInvalidLabelCharacter)) {
      return t('directorAnnotations.errors.invalidLabel');
    }
    if (mark.note.some(note => runeLength(note) > MAX_NOTE_RUNES)) {
      return t('directorAnnotations.errors.maxNoteRunes', { count: MAX_NOTE_RUNES });
    }
    if (mark.note.some(hasInvalidNoteCharacter)) {
      return t('directorAnnotations.errors.invalidNote');
    }
  }
  return null;
}

function errorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const candidate = error as { code?: string; body?: { code?: string } };
  return candidate.code || candidate.body?.code;
}

export default function DirectorAnnotationsPage() {
  const { t } = useTranslation('episodeDetail');
  const { projectId, id: episodeId } = useParams<{ projectId: string; id: string }>();
  const navigate = useNavigate();
  const textRef = useRef<HTMLDivElement>(null);
  const routeKey = `${projectId ?? ''}:${episodeId ?? ''}`;
  const routeKeyRef = useRef(routeKey);
  const routeGenerationRef = useRef(0);
  const loadRequestRef = useRef(0);
  const saveRequestRef = useRef(0);
  const savingRef = useRef(false);
  if (routeKeyRef.current !== routeKey) {
    routeKeyRef.current = routeKey;
    routeGenerationRef.current += 1;
  }
  const [workspace, setWorkspace] = useState<DirectorAnnotationWorkspace | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [marks, setMarks] = useState<DirectorMark[]>([]);
  const [editor, setEditor] = useState<EditorMark | null>(null);
  const [customLabel, setCustomLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [needsReload, setNeedsReload] = useState(false);
  const [loadError, setLoadError] = useState('');
  // 本次页面会话中保存过标注的集数（跨上一集/下一集切换保留），
  // 「完成并返回」时据此提示是否触发导演标签更新。
  const changedEpisodesRef = useRef<Set<string>>(new Set());
  const [finishPromptOpen, setFinishPromptOpen] = useState(false);
  const [updatingMapping, setUpdatingMapping] = useState(false);

  const load = useCallback(async () => {
    if (!episodeId || !projectId) return;
    const routeGeneration = routeGenerationRef.current;
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    savingRef.current = false;
    setSaving(false);
    setLoadError('');
    setFeedback('');
    setNeedsReload(false);
    setEditor(null);
    try {
      const [annotationData, episodeData] = await Promise.all([
        getDirectorAnnotations(episodeId),
        listEpisodes(projectId).catch(() => [] as Episode[]),
      ]);
      if (routeGenerationRef.current !== routeGeneration || loadRequestRef.current !== requestId) return;
      const normalizedMarks = annotationData.marks.map(normalizeMark);
      setWorkspace({ ...annotationData, marks: normalizedMarks });
      setMarks([...normalizedMarks].sort((a, b) => a.range.start - b.range.start || a.range.end - b.range.end));
      setEpisodes([...episodeData].sort((a, b) => a.index - b.index));
    } catch (error) {
      if (routeGenerationRef.current !== routeGeneration || loadRequestRef.current !== requestId) return;
      const code = errorCode(error);
      setWorkspace(null);
      setMarks([]);
      if (code === 'format_rewrite_required') {
        setLoadError(t('directorAnnotations.errors.formatRewriteRequired'));
      } else {
        setLoadError(error instanceof Error ? error.message : t('directorAnnotations.errors.loadFailed'));
      }
    } finally {
      if (routeGenerationRef.current === routeGeneration && loadRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [episodeId, projectId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const cancelDraft = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && editor?.isNew && !savingRef.current) {
        setEditor(null);
        setCustomLabel('');
        setFeedback('');
        window.getSelection()?.removeAllRanges();
      }
    };
    document.addEventListener('keydown', cancelDraft);
    return () => document.removeEventListener('keydown', cancelDraft);
  }, [editor]);

  const currentIndex = episodes.findIndex(item => item.id === episodeId);
  const currentEpisode = currentIndex >= 0 ? episodes[currentIndex] : null;
  const previousEpisode = currentIndex > 0 ? episodes[currentIndex - 1] : null;
  const nextEpisode = currentIndex >= 0 && currentIndex < episodes.length - 1 ? episodes[currentIndex + 1] : null;

  const renderedSegments = useMemo(() => {
    if (!workspace) return [];
    const codePoints = Array.from(workspace.baseContent);
    const segments: Array<{ text: string; mark?: DirectorMark }> = [];
    let cursor = 0;
    marks.forEach(mark => {
      if (mark.range.start > cursor) {
        segments.push({ text: codePoints.slice(cursor, mark.range.start).join('') });
      }
      segments.push({ text: codePoints.slice(mark.range.start, mark.range.end).join(''), mark });
      cursor = mark.range.end;
    });
    if (cursor < codePoints.length) segments.push({ text: codePoints.slice(cursor).join('') });
    return segments;
  }, [marks, workspace]);

  const beginSelection = () => {
    if (!workspace?.canEdit || savingRef.current || !textRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;
    const domRange = selection.getRangeAt(0);
    if (!textRef.current.contains(domRange.startContainer) || !textRef.current.contains(domRange.endContainer)) return;
    try {
      const range = selectionToCodePointRange(textRef.current, domRange);
      if (range.start === range.end) return;
      if (runeLength(Array.from(workspace.baseContent).slice(range.start, range.end).join('')) > MAX_MARK_RUNES) {
        setFeedback(t('directorAnnotations.errors.maxMarkRunes', { count: MAX_MARK_RUNES }));
        return;
      }
      if (marks.length >= MAX_MARKS) {
        setFeedback(t('directorAnnotations.errors.maxMarks', { count: MAX_MARKS }));
        return;
      }
      if (marks.some(mark => rangesOverlap(mark.range, range))) {
        setFeedback(t('directorAnnotations.errors.selectionOverlap'));
        return;
      }
      setEditor(cloneForEditor({
        id: newMarkId(),
        range,
        mark: Array.from(workspace.baseContent).slice(range.start, range.end).join(''),
        label: [],
        note: [],
      }, true));
      setCustomLabel('');
      setFeedback('');
    } catch {
      setFeedback(t('directorAnnotations.errors.selectionReadFailed'));
    } finally {
      selection.removeAllRanges();
    }
  };

  const chooseMark = (mark: DirectorMark) => {
    if (savingRef.current) return;
    setEditor(cloneForEditor(mark));
    setCustomLabel('');
    setFeedback('');
  };

  const persist = async (nextMarks: DirectorMark[]) => {
    if (!workspace || !episodeId || !workspace.canEdit || savingRef.current) return;
    const routeGeneration = routeGenerationRef.current;
    const requestId = ++saveRequestRef.current;
    const sorted = [...nextMarks].sort((a, b) => a.range.start - b.range.start || a.range.end - b.range.end);
    const validation = validateMarks(sorted, t);
    if (validation) {
      setFeedback(validation);
      return;
    }
    const payload = {
      expectedVersion: workspace.version,
      baseContentHash: workspace.baseContentHash,
      marks: sorted,
    };
    if (bodyByteLength(payload) > MAX_BODY_BYTES) {
      setFeedback(t('directorAnnotations.errors.bodyTooLarge'));
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setFeedback('');
    setNeedsReload(false);
    try {
      const saved = await saveDirectorAnnotations(episodeId, payload);
      if (routeGenerationRef.current !== routeGeneration || saveRequestRef.current !== requestId) return;
      const nextWorkspaceRaw = saved || {
        ...workspace,
        version: workspace.version + 1,
        marks: sorted,
      };
      const nextWorkspace = {
        ...nextWorkspaceRaw,
        marks: nextWorkspaceRaw.marks.map(normalizeMark),
      };
      setWorkspace(nextWorkspace);
      setMarks([...nextWorkspace.marks].sort((a, b) => a.range.start - b.range.start || a.range.end - b.range.end));
      setEditor(null);
      setCustomLabel('');
      setFeedback(t('directorAnnotations.saved'));
      changedEpisodesRef.current.add(episodeId);
    } catch (error) {
      if (routeGenerationRef.current !== routeGeneration || saveRequestRef.current !== requestId) return;
      const code = errorCode(error);
      if (code === 'annotation_version_conflict') {
        setFeedback(t('directorAnnotations.errors.versionConflict'));
        setNeedsReload(true);
      } else if (code === 'base_content_changed') {
        setFeedback(t('directorAnnotations.errors.baseChanged'));
        setNeedsReload(true);
      } else if (code === 'format_rewrite_required') {
        setFeedback(t('directorAnnotations.errors.formatRewriteRequired'));
      } else if (code === 'invalid_note') {
        setFeedback(t('directorAnnotations.errors.invalidNote'));
      } else {
        setFeedback(error instanceof Error ? error.message : t('directorAnnotations.errors.saveFailed'));
      }
    } finally {
      if (routeGenerationRef.current === routeGeneration && saveRequestRef.current === requestId) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  const saveEditor = () => {
    if (!editor || savingRef.current) return;
    const cleaned = cleanMark(editor);
    const nextMarks = editor.isNew
      ? [...marks, cleaned]
      : marks.map(mark => mark.id === editor.id ? cleaned : mark);
    persist(nextMarks);
  };

  const deleteEditor = () => {
    if (!editor || editor.isNew || savingRef.current) return;
    persist(marks.filter(mark => mark.id !== editor.id));
  };

  const toggleQuickLabel = (label: string) => {
    if (!editor || !workspace?.canEdit || savingRef.current) return;
    if (editor.label.includes(label)) {
      setEditor({ ...editor, label: editor.label.filter(value => value !== label) });
      return;
    }
    if (editor.label.length >= MAX_LABELS) {
      setFeedback(t('directorAnnotations.errors.maxLabels', { count: MAX_LABELS }));
      return;
    }
    setEditor({ ...editor, label: [...editor.label, label] });
    setFeedback('');
  };

  const addCustomLabel = () => {
    if (!editor || !workspace?.canEdit || savingRef.current) return;
    const label = customLabel.trim();
    if (!label) return;
    if (runeLength(label) > MAX_LABEL_RUNES) {
      setFeedback(t('directorAnnotations.errors.maxLabelRunes', { count: MAX_LABEL_RUNES }));
      return;
    }
    if (hasInvalidLabelCharacter(label)) {
      setFeedback(t('directorAnnotations.errors.invalidLabel'));
      return;
    }
    if (editor.label.length >= MAX_LABELS) {
      setFeedback(t('directorAnnotations.errors.maxLabels', { count: MAX_LABELS }));
      return;
    }
    setEditor({ ...editor, label: editor.label.includes(label) ? editor.label : [...editor.label, label] });
    setCustomLabel('');
    setFeedback('');
  };

  const navigateToEpisode = (episode: Episode | null) => {
    if (!episode || !projectId) return;
    navigate(`/projects/${encodeURIComponent(projectId)}/episodes/${encodeURIComponent(episode.id)}/director-annotations`);
  };

  const returnToScript = () => navigate(`/projects/${projectId}/script-summary`);

  const finishAndReturn = () => {
    if (changedEpisodesRef.current.size === 0) {
      returnToScript();
      return;
    }
    setFinishPromptOpen(true);
  };

  const confirmFinishUpdate = async () => {
    if (updatingMapping) return;
    setUpdatingMapping(true);
    const ids = Array.from(changedEpisodesRef.current);
    const results = await Promise.allSettled(ids.map(id => startDirectorMapping(id)));
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') changedEpisodesRef.current.delete(ids[index]);
    });
    const firstFailure = results.find(result => result.status === 'rejected') as PromiseRejectedResult | undefined;
    if (firstFailure) {
      setUpdatingMapping(false);
      setFinishPromptOpen(false);
      setFeedback(`${t('directorAnnotations.finishUpdateFailed')}：${actionErrorMessage(firstFailure.reason, t)}`);
      return;
    }
    returnToScript();
  };

  if (loading) return <div className="director-annotations-state">{t('directorAnnotations.loading')}</div>;

  if (!workspace) {
    return (
      <main className="director-annotations-state">
        <h1>{t('directorAnnotations.title')}</h1>
        <p role="alert">{loadError}</p>
        <button className="btn btn-secondary" type="button" onClick={() => navigate(`/projects/${projectId}/script-summary`)}>
          {t('directorAnnotations.backToScript')}
        </button>
      </main>
    );
  }

  return (
    <main className="director-annotations-page">
      <header className="director-annotations-header">
        <div className="director-annotations-title-group">
          <button className="btn btn-ghost" type="button" disabled={saving} onClick={() => navigate(`/projects/${projectId}/script-summary`)}>
            {t('directorAnnotations.backToScriptWithArrow')}
          </button>
          <h1>{t('directorAnnotations.episodeTitle', { index: currentEpisode?.index ?? '' })}</h1>
        </div>
        <div className="director-annotations-actions">
          <button className="btn btn-secondary" type="button" disabled={saving || !previousEpisode} onClick={() => navigateToEpisode(previousEpisode)}>{t('directorAnnotations.previousEpisode')}</button>
          <button className="btn btn-secondary" type="button" disabled={saving || !nextEpisode} onClick={() => navigateToEpisode(nextEpisode)}>{t('directorAnnotations.nextEpisode')}</button>
          <span className="director-annotations-save-state" aria-live="polite">{saving ? t('directorAnnotations.saving') : t('directorAnnotations.saved')}</span>
          <button className="btn btn-primary" type="button" disabled={saving} onClick={finishAndReturn}>{t('directorAnnotations.finish')}</button>
        </div>
      </header>

      {finishPromptOpen && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000 }}
            onClick={() => { if (!updatingMapping) setFinishPromptOpen(false); }}
          />
          <div
            role="dialog"
            aria-label={t('directorAnnotations.finishUpdateTitle')}
            style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: '420px', maxWidth: '90vw',
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px',
              display: 'flex', flexDirection: 'column', zIndex: 1001, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>
              {t('directorAnnotations.finishUpdateTitle')}
            </div>
            <div style={{ padding: '1rem', fontSize: '0.9rem', color: 'var(--text)' }}>
              {t('directorAnnotations.finishUpdatePrompt')}
            </div>
            <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn btn-secondary" type="button" disabled={updatingMapping} onClick={returnToScript}>
                {t('directorAnnotations.finishUpdateSkip')}
              </button>
              <button className="btn btn-primary" type="button" disabled={updatingMapping} onClick={confirmFinishUpdate}>
                {updatingMapping && <span className="loading-spinner" />}
                {t('directorAnnotations.finishUpdateConfirm')}
              </button>
            </div>
          </div>
        </>
      )}

      {workspace.invalidated && (
        <p className="director-annotations-notice">{t('directorAnnotations.invalidated')}</p>
      )}
      {!workspace.canEdit && <p className="director-annotations-notice">{t('directorAnnotations.readOnly')}</p>}
      {feedback && (
        <div className="director-annotations-feedback" role="alert">
          <span>{feedback}</span>
          {needsReload && <button className="btn btn-secondary" type="button" onClick={load}>{t('directorAnnotations.reload')}</button>}
        </div>
      )}

      <div className="director-annotations-workspace">
        <section className="director-annotations-text-panel" aria-label={t('directorAnnotations.rewrittenText')}>
          <div className="director-annotations-panel-heading">
            <h2>{t('directorAnnotations.rewrittenText')}</h2>
            <span>{t('directorAnnotations.markCount', { count: marks.length, max: MAX_MARKS })}</span>
          </div>
          <div
            ref={textRef}
            className="director-annotations-text"
            data-testid="annotation-text"
            onMouseUp={beginSelection}
          >
            {renderedSegments.map((segment, index) => segment.mark ? (
              <span
                key={segment.mark.id}
                className={`director-annotation-mark${editor?.id === segment.mark.id ? ' is-active' : ''}`}
                data-mark-id={segment.mark.id}
                role="button"
                aria-disabled={saving}
                tabIndex={saving ? -1 : 0}
                onClick={() => chooseMark(segment.mark!)}
                onKeyDown={event => {
                  if (!saving && (event.key === 'Enter' || event.key === ' ')) chooseMark(segment.mark!);
                }}
              >{segment.text}</span>
            ) : <span key={`text-${index}`}>{segment.text}</span>)}
          </div>
        </section>

        <aside className="director-annotations-editor" aria-label={t('directorAnnotations.details')}>
          <div className="director-annotations-panel-heading">
            <h2>{t('directorAnnotations.details')}</h2>
            {editor && <span>{editor.isNew ? t('directorAnnotations.newMark') : t('directorAnnotations.editMark')}</span>}
          </div>
          {!editor ? (
            <p className="director-annotations-empty">
              {workspace.canEdit ? t('directorAnnotations.emptyEditable') : t('directorAnnotations.emptyReadOnly')}
            </p>
          ) : (
            <div className="director-annotations-form">
              <blockquote>{editor.mark}</blockquote>

              <div className="director-annotations-field">
                <label>{t('directorAnnotations.labels')}</label>
                <div className="director-annotations-quick-labels">
                  {QUICK_LABELS.map(label => (
                    <button
                      key={label.value}
                      type="button"
                      className={`director-annotation-chip${editor.label.includes(label.value) ? ' is-selected' : ''}`}
                      disabled={!workspace.canEdit || saving}
                      onClick={() => toggleQuickLabel(label.value)}
                    >{t(`directorAnnotations.quickLabels.${label.key}`)}</button>
                  ))}
                </div>
                {editor.label.filter(label => !QUICK_LABEL_VALUES.includes(label)).length > 0 && (
                  <div className="director-annotations-custom-chips">
                    {editor.label.filter(label => !QUICK_LABEL_VALUES.includes(label)).map(label => (
                      <button
                        key={label}
                        type="button"
                        className="director-annotation-chip is-selected"
                        disabled={!workspace.canEdit || saving}
                        onClick={() => setEditor({ ...editor, label: editor.label.filter(value => value !== label) })}
                        title={workspace.canEdit ? t('directorAnnotations.removeLabel') : label}
                      >{label}</button>
                    ))}
                  </div>
                )}
                {workspace.canEdit && (
                  <input
                    className="input"
                    aria-label={t('directorAnnotations.customLabel')}
                    value={customLabel}
                    disabled={saving}
                    maxLength={MAX_LABEL_RUNES * 2}
                    placeholder={t('directorAnnotations.customLabelPlaceholder')}
                    onChange={event => setCustomLabel(event.target.value)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addCustomLabel();
                      }
                    }}
                  />
                )}
              </div>

              <div className="director-annotations-field">
                <div className="director-annotations-field-title">
                  <label>{t('directorAnnotations.notes')}</label>
                  {workspace.canEdit && (
                    <button
                      className="btn btn-ghost"
                      type="button"
                      disabled={saving || editor.note.length >= MAX_NOTES}
                      onClick={() => {
                        if (editor.note.length >= MAX_NOTES) {
                          setFeedback(t('directorAnnotations.errors.maxNotes', { count: MAX_NOTES }));
                        } else {
                          setEditor({ ...editor, note: [...editor.note, ''] });
                        }
                      }}
                    >{t('directorAnnotations.addNote')}</button>
                  )}
                </div>
                {editor.note.map((note, index) => (
                  <div className="director-annotations-note" key={index}>
                    <textarea
                      className="input"
                      aria-label={t('directorAnnotations.noteLabel', { index: index + 1 })}
                      value={note}
                      disabled={!workspace.canEdit || saving}
                      onChange={event => {
                        const next = [...editor.note];
                        next[index] = event.target.value;
                        setEditor({ ...editor, note: next });
                      }}
                    />
                    {workspace.canEdit && editor.note.length > 1 && (
                      <button
                        className="btn btn-ghost"
                        type="button"
                        aria-label={t('directorAnnotations.deleteNoteLabel', { index: index + 1 })}
                        disabled={saving}
                        onClick={() => setEditor({ ...editor, note: editor.note.filter((_, noteIndex) => noteIndex !== index) })}
                      >{t('directorAnnotations.remove')}</button>
                    )}
                  </div>
                ))}
              </div>

              {workspace.canEdit && (
                <div className="director-annotations-editor-actions">
                  {!editor.isNew && <button className="btn btn-danger" type="button" disabled={saving} onClick={deleteEditor}>{t('directorAnnotations.delete')}</button>}
                  {editor.isNew && <button className="btn btn-secondary" type="button" disabled={saving} onClick={() => setEditor(null)}>{t('directorAnnotations.cancel')}</button>}
                  <button className="btn btn-primary" type="button" disabled={saving} onClick={saveEditor}>
                    {editor.isNew ? t('directorAnnotations.save') : t('directorAnnotations.saveChanges')}
                  </button>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </main>
  );
}
